import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Star, ShieldCheck, Zap, Download, Users, XCircle, ArrowRight, Gift, Clock, CreditCard, Lock } from 'lucide-react';
import { PurchasePopup } from './components/PurchasePopup';
import { CountdownTimer } from './components/CountdownTimer';
import { FAQAccordion } from './components/FAQAccordion';
import { MetaPixel } from './components/MetaPixel';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  text,
  className = ''
}: {
  text: string;
  className?: string;
}) => <a href="https://checkout.fidelate.com/cart/57878430351734:1" target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-display font-bold text-base md:text-2xl py-3 md:py-4 px-6 md:px-12 rounded-full shadow-button active:shadow-button-active active:translate-y-1.5 transition-all w-full md:w-auto text-center leading-tight ${className}`} data-id="element-1">
    {text}
  </a>;
export function App() {
  return <div className="font-sans text-slate-800 bg-brand-light min-h-screen overflow-x-hidden" data-id="element-2">
      <MetaPixel data-id="element-3" />
      <PurchasePopup data-id="element-4" />

      {/* TOP BANNER */}
      <div className="bg-brand-navy text-white text-center py-1.5 md:py-2 px-4 text-xs md:text-sm font-medium" data-id="element-5">
        Todo un año de diversión sin móviles resuelto.
      </div>

      {/* HERO SECTION */}
      <section className="pt-6 pb-10 px-4 md:pt-16 md:pb-24 relative overflow-hidden" data-id="element-6">
        {/* Decorative background blobs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-brand-yellow/20 rounded-full blur-3xl -z-10" data-id="element-7"></div>
        <div className="absolute top-20 left-0 w-[400px] h-[400px] bg-brand-orange/10 rounded-full blur-3xl -z-10" data-id="element-8"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-brand-blue/10 rounded-full blur-3xl -z-10" data-id="element-9"></div>

        {/* Floating decorative emojis - hidden on mobile */}
        <div className="hidden md:block absolute top-32 left-8 text-5xl opacity-20 animate-bounce" style={{
        animationDuration: '3s'
      }} data-id="element-10">
          🎨
        </div>
        <div className="hidden md:block absolute top-48 right-12 text-5xl opacity-20 animate-bounce" style={{
        animationDuration: '4s',
        animationDelay: '0.5s'
      }} data-id="element-11">
          🧩
        </div>
        <div className="hidden md:block absolute bottom-32 left-16 text-5xl opacity-20 animate-bounce" style={{
        animationDuration: '3.5s',
        animationDelay: '1s'
      }} data-id="element-12">
          ✏️
        </div>
        <div className="hidden md:block absolute bottom-48 right-20 text-5xl opacity-20 animate-bounce" style={{
        animationDuration: '4.5s',
        animationDelay: '1.5s'
      }} data-id="element-13">
          🦖
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-14">
          <FadeIn data-id="element-15">
            <div className="inline-flex items-center gap-2 bg-white border-2 border-brand-orange/20 text-brand-orange font-bold px-4 py-1.5 md:px-5 md:py-2 rounded-full text-xs md:text-sm mb-4 md:mb-6 uppercase tracking-wider shadow-sm" data-id="element-16">
              <span className="w-2 h-2 bg-brand-orange rounded-full animate-pulse" data-id="element-17"></span>
              MEGA PACK SIN PANTALLAS™
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold text-brand-navy leading-[1.05] mb-6 md:mb-10 text-balance" data-id="element-18">
              El pack definitivo de actividades infantiles
            </h1>
          </FadeIn>

          {/* MOCKUP — now centered between title and subtitle, at ~70% size */}
          <FadeIn delay={0.15} data-id="element-19">
            <div className="relative mx-auto mb-6 md:mb-10 max-w-[240px] md:max-w-md" data-id="element-20">
              {/* Soft glow behind mockup */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-yellow/40 to-brand-orange/30 rounded-full blur-3xl scale-90 -z-10" data-id="element-21"></div>
              <img src="https://cdn.magicpatterns.com/uploads/bLkRK5NTNovbnw1FphuuxK/MOCK_UP_PRODUCTO_1-Photoroom.png" alt="Mega Pack Sin Pantallas Mockup" className="w-full drop-shadow-2xl hover:scale-105 transition-transform duration-500" data-id="element-22" />
            </div>
          </FadeIn>

          <FadeIn delay={0.25} data-id="element-23">
            <p className="text-sm md:text-xl text-gray-700 mb-6 md:mb-8 text-balance max-w-2xl mx-auto leading-relaxed" data-id="element-24">
              Para entretener, estimular y divertir a niños sin pantallas.
              Laberintos, sopas de letras, unir puntos, colorear, retos
              creativos y muchísimo más.{' '}
              <strong className="text-brand-navy" data-id="element-25">
                Sin preparar nada. Solo descargar, imprimir y usar.
              </strong>
            </p>

            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-6 md:mb-8" data-id="element-26">
              <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm text-xs md:text-sm font-semibold border border-gray-100" data-id="element-27">
                <Download size={14} className="text-brand-blue" data-id="element-28" /> 📄 PDF
                Digital
              </span>
              <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm text-xs md:text-sm font-semibold border border-gray-100" data-id="element-29">
                <Zap size={14} className="text-brand-orange" data-id="element-30" /> ⚡ Acceso
                Inmediato
              </span>
              <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full shadow-sm text-xs md:text-sm font-semibold border border-gray-100" data-id="element-31">
                <Users size={14} className="text-brand-green" data-id="element-32" /> 👦 4 a 8 años
              </span>
            </div>

            <div className="max-w-md mx-auto" data-id="element-33">
              <CTAButton text="💥 QUIERO MI MEGA PACK AHORA →" data-id="element-34" />
            </div>

            <div className="mt-4 md:mt-6 flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs md:text-sm text-gray-500 font-medium" data-id="element-35">
              <span className="flex items-center gap-1.5" data-id="element-36">
                <ShieldCheck size={16} className="text-brand-green" data-id="element-37" /> Garantía
                7 días
              </span>
              <span className="flex items-center gap-1.5" data-id="element-38">
                <Lock size={16} data-id="element-39" /> Pago seguro
              </span>
              <span className="flex items-center gap-1.5" data-id="element-40">
                <CreditCard size={16} data-id="element-41" /> Mercado Pago
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="relative py-6 md:py-10 overflow-hidden bg-gradient-to-r from-brand-navy via-indigo-900 to-brand-navy" data-id="element-42">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_50%,rgba(59,130,246,0.4),transparent_50%),radial-gradient(circle_at_80%_50%,rgba(255,107,0,0.25),transparent_50%)]" data-id="element-43"></div>
        <div className="absolute -top-20 left-1/4 w-72 h-72 bg-brand-blue/30 rounded-full blur-3xl" data-id="element-44"></div>
        <div className="absolute -bottom-20 right-1/4 w-72 h-72 bg-brand-orange/20 rounded-full blur-3xl" data-id="element-45"></div>
        <div className="max-w-6xl mx-auto px-2 md:px-4 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8 text-center md:divide-x md:divide-white/10 relative z-10" data-id="element-46">
          <div data-id="element-47">
            <div className="font-display font-bold text-2xl md:text-4xl text-brand-yellow md:mb-1" data-id="element-48">
              +1000
            </div>
            <div className="text-[10px] md:text-sm text-blue-100 uppercase tracking-wide font-medium" data-id="element-49">
              Familias interesadas
            </div>
          </div>
          <div data-id="element-50">
            <div className="font-display font-bold text-2xl md:text-4xl text-brand-yellow md:mb-1" data-id="element-51">
              500+
            </div>
            <div className="text-[10px] md:text-sm text-blue-100 uppercase tracking-wide font-medium" data-id="element-52">
              Páginas imprimibles
            </div>
          </div>
          <div data-id="element-53">
            <div className="font-display font-bold text-2xl md:text-4xl text-brand-yellow md:mb-1 flex items-center justify-center gap-1" data-id="element-54">
              4.9{' '}
              <Star size={18} className="fill-brand-yellow text-brand-yellow" data-id="element-55" />
            </div>
            <div className="text-[10px] md:text-sm text-blue-100 uppercase tracking-wide font-medium" data-id="element-56">
              Calificación media
            </div>
          </div>
          <div data-id="element-57">
            <div className="font-display font-bold text-2xl md:text-4xl md:mb-1" data-id="element-58">
              🌎
            </div>
            <div className="text-[10px] md:text-sm text-blue-100 uppercase tracking-wide font-medium" data-id="element-59">
              Todo el mundo
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS 1 */}
      <section className="py-10 md:py-20 px-4 bg-brand-light" data-id="element-60">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-4 md:gap-6" data-id="element-61">
          {[{
          text: 'Mi hijo estaba TODO el día con la tablet. Ahora cada tarde elige una actividad distinta y hasta me pide imprimir más páginas.',
          author: 'Mariana G.',
          role: 'Madrid — Mamá de 2 niños',
          initials: 'MG',
          color: 'bg-pink-500'
        }, {
          text: 'Uso las actividades en clase y me ahorran muchísimo tiempo. Los niños se entretienen y participan muchísimo más.',
          author: 'Carla R.',
          role: 'Buenos Aires — Maestra de primaria',
          initials: 'CR',
          color: 'bg-brand-blue'
        }, {
          text: 'Lo imprimimos para un viaje largo y literalmente salvó el trayecto. Entre laberintos, dibujos y juegos, ni tocaron el móvil.',
          author: 'Lucía F.',
          role: 'Barcelona — Mamá y niñera',
          initials: 'LF',
          color: 'bg-brand-orange'
        }].map((t, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white p-5 md:p-8 rounded-2xl shadow-card border border-gray-50 relative hover:-translate-y-1 hover:shadow-xl transition-all duration-300" data-id="element-62">
              <div className="absolute -top-3 left-4 text-6xl text-brand-yellow font-display font-bold leading-none select-none" data-id="element-63">
                "
              </div>
              <div className="flex gap-1 mb-2 md:mb-4 relative z-10 mt-2" data-id="element-64">
                {[...Array(5)].map((_, j) => <Star key={j} size={14} className="fill-brand-yellow text-brand-yellow" data-id="element-65" />)}
              </div>
              <p className="text-sm md:text-base text-gray-700 italic mb-4 md:mb-6 relative z-10" data-id="element-66">
                {t.text}
              </p>
              <div className="flex items-center gap-3 pt-3 md:pt-4 border-t border-gray-100" data-id="element-67">
                <div className={`w-9 h-9 md:w-11 md:h-11 rounded-full ${t.color} text-white font-bold flex items-center justify-center text-xs md:text-sm shrink-0`} data-id="element-68">
                  {t.initials}
                </div>
                <div data-id="element-69">
                  <div className="font-bold text-brand-navy text-sm md:text-base" data-id="element-70">
                    {t.author}
                  </div>
                  <div className="text-xs md:text-sm text-gray-500" data-id="element-71">
                    {t.role}
                  </div>
                </div>
              </div>
            </FadeIn>)}
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-10 md:py-20 px-4 bg-white" data-id="element-72">
        <div className="max-w-4xl mx-auto" data-id="element-73">
          <FadeIn className="text-center mb-6 md:mb-12" data-id="element-74">
            <h2 className="font-display text-2xl md:text-5xl font-bold text-brand-navy mb-2 md:mb-4" data-id="element-75">
              Si tenés niños cerca, esto te va a sonar familiar:
            </h2>
          </FadeIn>

          <div className="bg-red-50/50 rounded-2xl md:rounded-3xl p-5 md:p-12 border border-red-100" data-id="element-76">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-2.5 md:gap-y-4" data-id="element-77">
              {['El niño pasa horas pegado al móvil o la tablet', 'No sabés cómo entretenerlo sin pantallas', 'Terminás improvisando actividades todo el tiempo', 'Los juegos físicos duran 10 minutos y se aburren', 'Querés algo educativo pero que también sea divertido', 'Necesitás actividades rápidas para imprimir y usar', 'Los viajes, restaurantes o tardes en casa se vuelven un caos', 'Siempre terminás buscando ideas nuevas en Pinterest o YouTube', 'Querés que el niño use más la creatividad y menos el móvil'].map((point, i) => <FadeIn key={i} delay={i * 0.05} className="flex items-start gap-2.5 md:gap-3" data-id="element-78">
                  <XCircle className="text-red-500 shrink-0 mt-0.5" size={18} data-id="element-79" />
                  <span className="text-sm md:text-base text-gray-700 font-medium" data-id="element-80">
                    {point}
                  </span>
                </FadeIn>)}
            </div>
          </div>

          <FadeIn delay={0.4} className="text-center mt-6 md:mt-12" data-id="element-81">
            <h3 className="font-display text-xl md:text-3xl font-bold text-brand-orange" data-id="element-82">
              ¿Cuánto tiempo más va a ganar la pantalla?
            </h3>
          </FadeIn>
        </div>
      </section>

      {/* SOLUTION / FEATURES */}
      <section className="py-10 md:py-20 px-4 bg-brand-navy text-white relative overflow-hidden" data-id="element-83">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-blue/20 rounded-full blur-3xl -z-0" data-id="element-84"></div>
        <div className="max-w-5xl mx-auto relative z-10" data-id="element-85">
          <FadeIn className="text-center mb-8 md:mb-16" data-id="element-86">
            <div className="text-brand-yellow font-bold tracking-widest uppercase text-[10px] md:text-sm mb-2 md:mb-4" data-id="element-87">
              SISTEMA N°1 DE ACTIVIDADES INFANTILES IMPRIMIBLES
            </div>
            <h2 className="font-display text-2xl md:text-6xl font-bold mb-3 md:mb-6" data-id="element-88">
              FIDELATE.COM™ — MEGA PACK SIN PANTALLAS™
            </h2>
            <p className="text-base md:text-2xl text-blue-100" data-id="element-89">
              500+ páginas de entretenimiento educativo listas para imprimir
            </p>
          </FadeIn>

          <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-8 md:mb-16" data-id="element-90">
            {['🎨 Colorear', '🧩 Laberintos', '🔎 Sopas de letras', '✏️ Unir puntos', '👀 Encontrar diferencias', '🧠 Retos creativos', '😂 Preguntas divertidas', '✂️ Recortar y pegar', '📚 Juegos educativos', '🎲 Actividades visuales'].map((pill, i) => <FadeIn key={i} delay={i * 0.05} className="bg-white/10 backdrop-blur-sm border border-white/20 px-3 py-1.5 md:px-4 md:py-2 rounded-full font-medium text-xs md:text-base hover:bg-white/20 transition-colors cursor-default" data-id="element-91">
                {pill}
              </FadeIn>)}
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-center bg-white/5 rounded-2xl md:rounded-3xl p-5 md:p-12 border border-white/10" data-id="element-92">
            <div data-id="element-93">
              <h3 className="font-display text-lg md:text-2xl font-bold text-brand-yellow mb-3 md:mb-6" data-id="element-94">
                Funciona desde hoy mismo en:
              </h3>
              <ul className="space-y-2 md:space-y-3 text-sm md:text-lg" data-id="element-95">
                {['casa,', 'escuela,', 'viajes,', 'cumpleaños,', 'restaurantes,', 'guarderías,', 'actividades grupales.'].map((item, i) => <li key={i} className="flex items-center gap-2 md:gap-3" data-id="element-96">
                    <CheckCircle2 className="text-brand-green shrink-0" size={18} data-id="element-97" />
                    <span className="capitalize" data-id="element-98">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div data-id="element-99">
              <h3 className="font-display text-lg md:text-2xl font-bold text-brand-yellow mb-3 md:mb-6" data-id="element-100">
                Temáticas incluidas:
              </h3>
              <div className="grid grid-cols-2 gap-2 md:gap-3" data-id="element-101">
                {['🦖 Dinosaurios', '🚀 Espacio', '🐶 Animales', '🦄 Unicornios', '🌊 Océano', '🚗 Vehículos', '🎃 Halloween', '🎄 Navidad', '☀️ Verano', '🤖 Robots', '👑 Princesas', '🌈 Fantasía'].map((theme, i) => <div key={i} className="bg-white/10 rounded-lg px-2.5 py-1.5 md:px-3 md:py-2 text-xs md:text-sm font-medium" data-id="element-102">
                    {theme}
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED & BONUSES */}
      <section className="py-10 md:py-20 px-4 bg-gradient-to-b from-brand-light to-white relative overflow-hidden" data-id="element-103">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-brand-yellow/10 rounded-full blur-3xl -translate-y-1/2" data-id="element-104"></div>
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-brand-orange/10 rounded-full blur-3xl -translate-y-1/2" data-id="element-105"></div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-4 md:gap-8 relative z-10" data-id="element-106">
          {/* Main Pack */}
          <FadeIn className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-10 shadow-card border border-gray-100" data-id="element-107">
            <div className="w-12 h-12 md:w-16 md:h-16 bg-brand-blue/10 rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-6" data-id="element-108">
              <span className="text-2xl md:text-4xl" data-id="element-109">📘</span>
            </div>
            <h2 className="font-display text-xl md:text-3xl font-bold text-brand-navy mb-1 md:mb-2" data-id="element-110">
              EL MEGA PACK INCLUYE
            </h2>
            <h3 className="text-sm md:text-xl text-gray-500 font-medium mb-4 md:mb-8" data-id="element-111">
              Mega Biblioteca de Actividades
            </h3>

            <ul className="space-y-2.5 md:space-y-4" data-id="element-112">
              {['500+ páginas imprimibles', 'Laberintos de diferentes dificultades', 'Sopas de letras infantiles', 'Unir puntos y dibujo guiado', 'Actividades para colorear', 'Juegos visuales y educativos', 'Retos creativos para estimular imaginación', 'Actividades rápidas para cualquier momento', 'Material ideal para padres y profesores', 'Organizado por categorías y temáticas'].map((item, i) => <li key={i} className="flex items-start gap-2.5 md:gap-3" data-id="element-113">
                  <CheckCircle2 className="text-brand-blue shrink-0 mt-0.5" size={18} data-id="element-114" />
                  <span className="text-sm md:text-base text-gray-700 font-medium" data-id="element-115">
                    {item}
                  </span>
                </li>)}
            </ul>
          </FadeIn>

          {/* Bonuses */}
          <FadeIn delay={0.2} className="bg-gradient-to-br from-brand-yellow/20 to-brand-orange/10 rounded-2xl md:rounded-3xl p-5 md:p-10 shadow-card border border-brand-yellow/30 relative overflow-hidden" data-id="element-116">
            <div className="absolute top-0 right-0 bg-red-500 text-white font-bold text-[10px] md:text-xs py-1 px-8 rotate-45 translate-x-6 translate-y-4 shadow-md" data-id="element-117">
              GRATIS HOY
            </div>
            <div className="w-12 h-12 md:w-16 md:h-16 bg-brand-orange/10 rounded-xl md:rounded-2xl flex items-center justify-center mb-3 md:mb-6" data-id="element-118">
              <Gift className="text-brand-orange" size={24} data-id="element-119" />
            </div>
            <h2 className="font-display text-xl md:text-3xl font-bold text-brand-navy mb-4 md:mb-8" data-id="element-120">
              BONUS INCLUIDOS GRATIS
            </h2>

            <ul className="space-y-2 md:space-y-5 mb-4 md:mb-8" data-id="element-121">
              {['Pack Extra de Actividades para Viajes', 'Pack "Días de Lluvia"', 'Actividades Rápidas de 5 Minutos', 'Mini Juegos para Imprimir', 'Pack Especial "Sin Tablet"', 'Guía de Uso para Padres y Profesores'].map((item, i) => <li key={i} className="flex items-center gap-2 md:gap-3 bg-white/60 p-2 md:p-3 rounded-lg md:rounded-xl border border-white" data-id="element-122">
                  <span className="text-lg md:text-2xl" data-id="element-123">🎁</span>
                  <span className="text-brand-navy font-bold text-sm md:text-base" data-id="element-124">
                    {item}
                  </span>
                </li>)}
            </ul>

            <div className="bg-white rounded-xl p-3 md:p-4 text-center border-2 border-brand-orange border-dashed" data-id="element-125">
              <p className="text-gray-500 font-medium text-xs md:text-sm md:mb-1" data-id="element-126">
                🔥 VALOR TOTAL DE LOS BONUS:
              </p>
              <p className="font-display text-lg md:text-2xl font-bold text-brand-navy line-through decoration-red-500 decoration-2" data-id="element-127">
                $49.000
              </p>
              <p className="font-display text-2xl md:text-3xl font-extrabold text-brand-orange uppercase" data-id="element-128">
                GRATIS
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="comprar" className="py-10 md:py-20 px-4 bg-white relative" data-id="element-129">
        <div className="max-w-3xl mx-auto text-center" data-id="element-130">
          <FadeIn data-id="element-131">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-600 font-bold px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm mb-4 md:mb-6" data-id="element-132">
              <Clock size={14} data-id="element-133" /> EL PRECIO DE LANZAMIENTO TERMINA EN:
            </div>

            <div className="mb-4 md:mb-8" data-id="element-134">
              <CountdownTimer data-id="element-135" />
            </div>

            <p className="text-xs md:text-base text-gray-500 font-medium mb-6 md:mb-10 flex items-center justify-center gap-2" data-id="element-136">
              ⚠️ Después sube y no vuelve a bajar.
            </p>

            <div className="bg-white rounded-2xl md:rounded-[2rem] shadow-2xl border-4 border-brand-navy p-5 md:p-12 relative transform hover:-translate-y-2 transition-transform duration-300" data-id="element-137">
              <div className="absolute -top-3 md:-top-5 left-1/2 -translate-x-1/2 bg-brand-orange text-white font-bold py-1 px-4 md:py-1.5 md:px-6 rounded-full text-xs md:text-sm uppercase tracking-wider shadow-md whitespace-nowrap" data-id="element-138">
                Oferta Especial
              </div>

              <h3 className="font-display text-lg md:text-3xl font-bold text-brand-navy mb-1 md:mb-2 mt-2 md:mt-0" data-id="element-139">
                📘 MEGA PACK SIN PANTALLAS™
              </h3>
              <p className="text-xs md:text-base text-gray-500 font-medium mb-4 md:mb-8" data-id="element-140">
                500+ Páginas + Todos los Bonus
              </p>

              <div className="flex flex-col items-center justify-center mb-4 md:mb-8" data-id="element-141">
                <span className="text-lg md:text-2xl text-gray-400 line-through font-bold mb-1 md:mb-2" data-id="element-142">
                  $39.990
                </span>
                <div className="flex items-start text-brand-navy" data-id="element-143">
                  <span className="text-2xl md:text-4xl font-bold mt-1 md:mt-2" data-id="element-144">
                    $
                  </span>
                  <span className="text-5xl md:text-8xl font-display font-extrabold leading-none tracking-tighter" data-id="element-145">
                    19.990
                  </span>
                </div>
              </div>

              <CTAButton text="💥 SÍ, QUIERO EL MEGA PACK →" className="w-full mb-3 md:mb-6" data-id="element-146" />

              <div className="flex flex-wrap justify-center gap-2 md:gap-4 text-[10px] md:text-sm text-gray-500 font-medium bg-gray-50 py-2 md:py-3 px-3 md:px-4 rounded-xl" data-id="element-147">
                <span className="flex items-center gap-1" data-id="element-148">
                  <Lock size={12} data-id="element-149" /> Pago seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-150">
                  <Zap size={12} className="text-brand-orange" data-id="element-151" /> Acceso
                  inmediato
                </span>
                <span className="flex items-center gap-1" data-id="element-152">
                  <ShieldCheck size={12} className="text-brand-green" data-id="element-153" />{' '}
                  Garantía 7 días
                </span>
                <span className="flex items-center gap-1" data-id="element-154">
                  <CreditCard size={12} data-id="element-155" /> Mercado Pago
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* TESTIMONIALS 2 */}
      <section className="py-10 md:py-20 px-4 bg-brand-light" data-id="element-156">
        <div className="max-w-6xl mx-auto" data-id="element-157">
          <h2 className="font-display text-xl md:text-3xl font-bold text-center text-brand-navy mb-6 md:mb-12" data-id="element-158">
            Lo que dicen quienes ya lo usan:
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6" data-id="element-159">
            {[{
            text: 'Mi hijo dejó de pedirme el móvil apenas vio los laberintos y los juegos de dinosaurios.',
            author: 'Camila T.',
            role: 'Málaga — Mamá'
          }, {
            text: 'Lo uso todas las semanas en el aula. Tener tantas páginas listas me ahorra muchísimo tiempo.',
            author: 'Andrea P.',
            role: 'Sevilla — Profesora'
          }, {
            text: 'El pack parece infinito. Siempre encontramos algo nuevo para hacer.',
            author: 'Natalia R.',
            role: 'Valencia — Mamá de 3 niños'
          }, {
            text: 'Las actividades creativas son buenísimas para sacarlos de la rutina.',
            author: 'Paula M.',
            role: 'Córdoba — Niñera'
          }].map((t, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-sm border border-gray-100" data-id="element-160">
                <div className="flex gap-0.5 md:gap-1 mb-2 md:mb-3" data-id="element-161">
                  {[...Array(5)].map((_, j) => <Star key={j} size={12} className="fill-brand-yellow text-brand-yellow" data-id="element-162" />)}
                </div>
                <p className="text-gray-700 text-xs md:text-sm italic mb-3 md:mb-4" data-id="element-163">
                  "{t.text}"
                </p>
                <div data-id="element-164">
                  <div className="font-bold text-brand-navy text-xs md:text-sm" data-id="element-165">
                    {t.author}
                  </div>
                  <div className="text-[10px] md:text-xs text-gray-500" data-id="element-166">
                    {t.role}
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-10 md:py-20 px-4 bg-brand-navy text-white text-center" data-id="element-167">
        <div className="max-w-3xl mx-auto" data-id="element-168">
          <FadeIn data-id="element-169">
            <ShieldCheck size={48} className="text-brand-yellow mx-auto mb-3 md:mb-6 md:w-16 md:h-16" data-id="element-170" />
            <h2 className="font-display text-2xl md:text-5xl font-bold mb-4 md:mb-6" data-id="element-171">
              GARANTÍA MEGA PACK SIN PANTALLAS™ — 7 DÍAS
            </h2>
            <div className="text-sm md:text-xl text-blue-100 space-y-2 md:space-y-4 mb-4 md:mb-8" data-id="element-172">
              <p data-id="element-173">
                Descargás el pack. Revisás las 500+ páginas. Probás las
                actividades.
              </p>
              <p className="font-bold text-white text-lg md:text-2xl" data-id="element-174">
                Si en 7 días no te encanta:
              </p>
              <p className="text-brand-yellow font-bold text-lg md:text-2xl" data-id="element-175">
                Te devolvemos el 100% del dinero.
              </p>
              <p data-id="element-176">Sin preguntas. Sin complicaciones.</p>
            </div>
            <p className="text-xs md:text-sm text-gray-400 uppercase tracking-widest font-medium" data-id="element-177">
              Miles de familias ya están usando actividades sin pantallas todos
              los días.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-20 px-4 bg-brand-light" data-id="element-178">
        <div className="max-w-3xl mx-auto" data-id="element-179">
          <FadeIn className="text-center mb-6 md:mb-12" data-id="element-180">
            <h2 className="font-display text-2xl md:text-4xl font-bold text-brand-navy mb-2 md:mb-4" data-id="element-181">
              PREGUNTAS FRECUENTES
            </h2>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-182">
            <FAQAccordion data-id="element-183" />
          </FadeIn>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-10 md:py-20 px-4 bg-white" data-id="element-184">
        <div className="max-w-5xl mx-auto" data-id="element-185">
          <FadeIn className="text-center mb-8 md:mb-16" data-id="element-186">
            <h2 className="font-display text-2xl md:text-4xl font-bold text-brand-navy mb-3 md:mb-4" data-id="element-187">
              MEGA PACK SIN PANTALLAS™
            </h2>
            <p className="text-sm md:text-xl text-gray-600 mb-1 md:mb-2" data-id="element-188">
              Mañana el niño puede volver a pasar horas mirando una pantalla.
            </p>
            <p className="text-sm md:text-xl text-gray-600 font-bold" data-id="element-189">
              O podés tener 500+ actividades listas para imprimir desde hoy.
            </p>
            <h3 className="font-display text-lg md:text-2xl font-bold text-brand-orange mt-4 md:mt-8" data-id="element-190">
              ¿Cuánto cuesta NO tenerlo?
            </h3>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8" data-id="element-191">
            <FadeIn delay={0.1} className="bg-red-50 rounded-2xl md:rounded-3xl p-5 md:p-8 border border-red-100" data-id="element-192">
              <h4 className="font-display text-lg md:text-2xl font-bold text-red-600 mb-3 md:mb-6 text-center" data-id="element-193">
                SIN:
              </h4>
              <ul className="space-y-2 md:space-y-4" data-id="element-194">
                {['📱 Horas y horas frente al móvil', '😓 Aburrimiento constante', '🧠 Poco estímulo creativo', '📚 Falta de actividades educativas', '🚗 Viajes difíciles', '☔ Tardes caóticas', '📄 Buscar ideas todos los días', '😵 Improvisar constantemente'].map((item, i) => <li key={i} className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-gray-700 font-medium" data-id="element-195">
                    <XCircle className="text-red-400 shrink-0" size={18} data-id="element-196" />{' '}
                    {item.substring(2)}
                  </li>)}
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-green-50 rounded-2xl md:rounded-3xl p-5 md:p-8 border border-green-100 shadow-lg transform md:-translate-y-4" data-id="element-197">
              <h4 className="font-display text-lg md:text-2xl font-bold text-brand-green mb-3 md:mb-6 text-center" data-id="element-198">
                CON:
              </h4>
              <ul className="space-y-2 md:space-y-4" data-id="element-199">
                {['🎨 500+ páginas listas para imprimir', '🧩 Actividades educativas y divertidas', '⚡ Uso inmediato', '👦 Menos pantallas', '🧠 Más creatividad y aprendizaje', '🚗 Ideal para viajes y salidas', '🏡 Perfecto para casa o escuela', '✨ Diversión durante meses'].map((item, i) => <li key={i} className="flex items-center gap-2 md:gap-3 text-sm md:text-base text-brand-navy font-bold" data-id="element-200">
                    <CheckCircle2 className="text-brand-green shrink-0" size={18} data-id="element-201" />{' '}
                    {item.substring(2)}
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-12 md:py-24 px-4 bg-gradient-to-br from-brand-yellow via-brand-yellow to-amber-300 relative overflow-hidden" data-id="element-202">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-brand-orange via-transparent to-transparent" data-id="element-203"></div>
        <div className="hidden md:block absolute top-10 left-10 text-6xl opacity-30 animate-bounce" style={{
        animationDuration: '3s'
      }} data-id="element-204">
          🎨
        </div>
        <div className="hidden md:block absolute top-20 right-20 text-6xl opacity-30 animate-bounce" style={{
        animationDuration: '4s',
        animationDelay: '0.5s'
      }} data-id="element-205">
          🧩
        </div>
        <div className="hidden md:block absolute bottom-16 left-16 text-6xl opacity-30 animate-bounce" style={{
        animationDuration: '3.5s',
        animationDelay: '1s'
      }} data-id="element-206">
          ✨
        </div>
        <div className="hidden md:block absolute bottom-10 right-10 text-6xl opacity-30 animate-bounce" style={{
        animationDuration: '4.5s',
        animationDelay: '1.5s'
      }} data-id="element-207">
          🚀
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-208">
          <FadeIn data-id="element-209">
            <h2 className="font-display text-2xl md:text-5xl font-extrabold text-brand-navy mb-3 md:mb-4 uppercase tracking-tight" data-id="element-210">
              NO DEJES PASAR ESTA OPORTUNIDAD
            </h2>

            <div className="bg-white rounded-2xl md:rounded-[2rem] shadow-2xl p-5 md:p-12 mt-5 md:mt-10 max-w-xl mx-auto" data-id="element-211">
              <h3 className="font-display text-lg md:text-2xl font-bold text-brand-navy mb-1 md:mb-2" data-id="element-212">
                📘 MEGA PACK SIN PANTALLAS™
              </h3>
              <p className="text-xs md:text-base text-gray-500 font-medium mb-3 md:mb-6" data-id="element-213">
                500+ Páginas + Todos los Bonus
              </p>

              <div className="flex flex-col items-center justify-center mb-4 md:mb-8" data-id="element-214">
                <span className="text-base md:text-xl text-gray-400 line-through font-bold mb-1" data-id="element-215">
                  $39.990
                </span>
                <div className="flex items-start text-brand-navy" data-id="element-216">
                  <span className="text-2xl md:text-3xl font-bold mt-1 md:mt-2" data-id="element-217">
                    $
                  </span>
                  <span className="text-5xl md:text-6xl font-display font-extrabold leading-none tracking-tighter" data-id="element-218">
                    19.990
                  </span>
                </div>
              </div>

              <CTAButton text="💥 QUIERO MI MEGA PACK →" className="w-full mb-3 md:mb-6" data-id="element-219" />

              <div className="flex flex-wrap justify-center gap-2 md:gap-4 text-[10px] md:text-xs text-gray-500 font-medium" data-id="element-220">
                <span className="flex items-center gap-1" data-id="element-221">
                  <Lock size={12} data-id="element-222" /> Pago seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-223">
                  <Zap size={12} className="text-brand-orange" data-id="element-224" /> Acceso
                  inmediato
                </span>
                <span className="flex items-center gap-1" data-id="element-225">
                  <ShieldCheck size={12} className="text-brand-green" data-id="element-226" />{' '}
                  Garantía 7 días
                </span>
                <span className="flex items-center gap-1" data-id="element-227">
                  <CreditCard size={12} data-id="element-228" /> Mercado Pago
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-brand-navy text-gray-400 py-8 md:py-12 px-4 text-center border-t border-white/10" data-id="element-229">
        <div className="max-w-4xl mx-auto" data-id="element-230">
          <h4 className="font-display text-xl md:text-2xl font-bold text-white mb-2 md:mb-4 tracking-wider" data-id="element-231">
            FIDELATE.COM™
          </h4>
          <p className="text-sm md:text-lg text-gray-300 mb-1 md:mb-2" data-id="element-232">
            La biblioteca definitiva de actividades infantiles imprimibles.
          </p>
          <p className="text-xs md:text-base mb-5 md:mb-8 italic" data-id="element-233">
            Porque entretener niños sin pantallas no debería ser una batalla
            diaria. 🎨🧩✨
          </p>

          <div className="flex flex-wrap justify-center gap-3 md:gap-6 text-xs md:text-sm mb-5 md:mb-8" data-id="element-234">
            <a href="#" className="hover:text-white transition-colors" data-id="element-235">
              fidelate042@gmail.com
            </a>
            <span className="hidden md:inline" data-id="element-236">•</span>
            <a href="#" className="hover:text-white transition-colors" data-id="element-237">
              @fidelate
            </a>
            <span className="hidden md:inline" data-id="element-238">•</span>
            <a href="#" className="hover:text-white transition-colors" data-id="element-239">
              fidelate.com
            </a>
          </div>

          <p className="text-[10px] md:text-xs" data-id="element-240">
            © Fidelate.com™ 2026 — Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}